import { Component } from 'react';
import { router } from '@inertiajs/react';

/**
 * Global React Error Boundary for the Inertia app.
 * Catches unhandled JavaScript errors in the React tree (render/effect crashes)
 * and shows a consistent error experience via the Inertia Errors/Generic page.
 *
 * Uses a class component because Error Boundaries must be class-based (no hook equivalent).
 */
class ErrorBoundary extends Component {
    state = {
        hasError: false,
        error: null,
    };

    /**
     * Updates state so the next render shows a fallback instead of the broken tree.
     * Called during the render phase when a child throws.
     */
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    /**
     * Called after an error has been thrown. We log here and trigger navigation in production.
     * Side effects (logging, redirect) belong in componentDidCatch, not getDerivedStateFromError.
     */
    componentDidCatch(error, errorInfo) {
        console.error('[ErrorBoundary] Unhandled React error:', error, errorInfo);

        // Placeholder for future Sentry/Bugsnag integration:
        // Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });

        const isProduction = import.meta.env.PROD;
        if (isProduction) {
            // Navigate to the generic error page without pushing to history (replace).
            // No page reload; Inertia fetches the /error page and swaps the view.
            router.replace('/error');
        }
        // In development we do not redirect; the boundary is not mounted (see app.jsx),
        // so React's error overlay and stack trace remain visible.
    }

    render() {
        if (this.state.hasError && this.state.error) {
            // Production: show nothing until Inertia completes the replace to /error.
            // Avoids flashing the broken UI. In dev this path is not reached (no wrap).
            return null;
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
